import React, { useState, useMemo, useEffect, useRef } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import {
  MapPin,
  Globe,
  Target,
  AlertTriangle,
  CheckCircle,
  Users,
  Grid,
  Navigation,
  Compass,
  Activity,
  ArrowUpDown,
  Download,
  Info
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './GeolocationSection.css';
import PulseBeacon from './PulseBeacon';
import CollapsibleSection from './CollapsibleSection';
import EmptyStateCelebration from './EmptyStateCelebration';

import CoachingPanel from './CoachingPanel';
const GeolocationSection = ({ data, dataCollectorId, coachingSuggestions = [], isSidePanelExpanded, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Map controls
  const [selectedCollector, setSelectedCollector] = useState('all');
  const [showAnomalies, setShowAnomalies] = useState(true);
  
  // Collector table sorting
  const [collectorSortField, setCollectorSortField] = useState('location_count');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');

  // Leaflet map refs
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Memoized sorted collectors - MUST be at top level
  const sortedCollectors = useMemo(() => {
    const byCollector = data?.overall?.by_collector || {};
    return Object.entries(byCollector)
      .map(([id, collectorData]) => ({ id, ...collectorData }))
      .sort((a, b) => {
        let aVal, bVal;
        if (collectorSortField === 'cluster_percentage') {
          aVal = a.clustering?.cluster_percentage ?? 0;
          bVal = b.clustering?.cluster_percentage ?? 0;
        } else if (collectorSortField === 'coverage_area_km2') {
          aVal = a.coverage_area_km2 ?? 0;
          bVal = b.coverage_area_km2 ?? 0;
        } else {
          aVal = a[collectorSortField] ?? 0;
          bVal = b[collectorSortField] ?? 0;
        }
        return collectorSortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      });
  }, [data?.overall?.by_collector, collectorSortField, collectorSortDirection]);

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    neutral: '#6B7280',
  };

  // Early return if no data
  if (!data?.overall) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div className="nsx-metric-card" style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <MapPin size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Geolocation Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Configure latitude and longitude variables to analyze geolocation data.
          </p>
        </div>
      </div>
    );
  }

  // Extract data
  const overall = data.overall;
  const qualityScore = overall.quality_score || { overall_score: 0, component_scores: {}, interpretation: {} };
  const spatialAnalysis = overall.spatial_analysis || {};
  const clustering = overall.clustering || {};
  const distanceMetrics = overall.distance_metrics || {};
  const anomalies = overall.anomalies || {};
  const groupOutliers = overall.group_outliers || {};
  const boundaryCheck = overall.boundary_check || {};
  const bounds = overall.bounds || {};
  const locations = overall.locations || [];
  const byCollector = overall.by_collector || {};
  const collectorComparison = overall.collector_comparison || {};
  const summary = overall.summary || {};

  // Utility functions
  const getRatingColor = (rating) => {
    const ratingColors = {
      'Excellent': '#10B981',
      'Good': '#34D399',
      'Moderate': '#F59E0B',
      'Poor': '#F97316',
      'Critical': '#EF4444'
    };
    return ratingColors[rating] || colors.textMuted;
  };

  const getSeverityColor = (severity) => {
    const severityColors = {
      'high': '#EF4444',
      'moderate': '#F59E0B',
      'low': '#10B981'
    };
    return severityColors[severity] || colors.textMuted;
  };

  // Dynamic color based on a 0-100 score
  const getScoreColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#34D399';
    if (score >= 40) return '#F59E0B';
    if (score >= 20) return '#F97316';
    return '#EF4444';
  };

  const formatNumber = (num, decimals = 1) => {
    if (num === undefined || num === null) return '—';
    return Number(num).toFixed(decimals);
  };

  // Score Ring Component
  const ScoreRing = ({ score, size = 160, strokeWidth = 12 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const rating = qualityScore.interpretation?.rating || 'N/A';
    const ratingColor = getRatingColor(rating);
    const ringReady = useAnimateOnMount(400);

    return (
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.border}
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={ratingColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={ringReady ? offset : circumference}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: '700', color: ratingColor }}>
            {Math.round(score)}
          </div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: ratingColor,
            backgroundColor: `${ratingColor}15`,
            padding: '4px 12px',
            borderRadius: '12px',
            marginTop: '4px'
          }}>
            {rating}
          </div>
        </div>
      </div>
    );
  };

  // Metric Card Component
  const MetricCard = ({ icon: Icon, title, value, subtitle, color = colors.primary, trend, severity, animDelay = 0 }) => {
    const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
    const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
    const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');
    const trendPositive = trend !== undefined && trend >= 0;
    const trendColor = trendPositive
      ? (isDarkMode ? '#34d399' : '#047857')
      : (isDarkMode ? '#f87171' : '#b91c1c');

    return (
      <AnimatedCard delay={animDelay} translateY={12}>
      <div
        className="nsx-metric-card"
        style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}
      >
        {severity && (
          <svg
            width="30" height="30" viewBox="0 0 30 30"
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
          >
            <path
              d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
              stroke={iconColor} strokeWidth="2" fill="none"
              strokeLinecap="round" opacity={0.9}
            />
          </svg>
        )}

        <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

        {/* Trend chip — top-right, hides when PulseBeacon showing (severity takes priority) */}
        {trend !== undefined && !severity && (
          <div style={{
            position: 'absolute', top: '10px', right: '10px',
            padding: '2px 8px',
            borderRadius: '100px',
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.02em',
            background: isDarkMode ? `${trendColor}22` : `${trendColor}14`,
            color: trendColor,
            border: `0.5px solid ${trendColor}33`,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {trendPositive ? '+' : ''}{trend}%
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '5px',
            background: severity
              ? (isDarkMode
                  ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                  : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
              : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: iconColor,
          }}>
            <Icon size={16} strokeWidth={1.75} />
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 500,
              letterSpacing: '0.16em',
              textTransform: 'lowercase',
              color: colors.textMuted,
              marginBottom: '10px',
            }}>
              {title}
            </div>
            <div
              className="metric-value"
              style={{
                fontSize: '32px',
                fontWeight: 500,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                color: colors.text,
                lineHeight: 1.05,
                marginBottom: '6px',
              }}
            >
              <AnimatedNumber value={value} duration={800} />
            </div>
            {subtitle && (
              <div style={{
                fontSize: '11.5px',
                fontWeight: 400,
                letterSpacing: '-0.005em',
                color: colors.textMuted,
                lineHeight: 1.4,
              }}>
                {subtitle}
              </div>
            )}
          </div>
          <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
        </div>
      </div>
      </AnimatedCard>
    );
  };

  // Tab Button Component - consistent with SurveyFlowDashboard
  // Tab definitions for SubTabs
  const tabs = [
    { id: 'overview', label: 'Location Overview', icon: MapPin },
    { id: 'spatial', label: 'Spatial Analysis', icon: Grid },
    { id: 'collector', label: 'Collector Analysis', icon: Users },
  ];

  // Sortable Header Component
  const SortableHeader = ({ field, label }) => (
    <th
      onClick={() => {
        if (collectorSortField === field) {
          setCollectorSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
          setCollectorSortField(field);
          setCollectorSortDirection('desc');
        }
      }}
      style={{
        padding: '14px 16px',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '450',
        color: colors.textSecondary,
        borderBottom: `2px solid ${colors.border}`,
        cursor: 'pointer',
        userSelect: 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        {label}
        <ArrowUpDown size={14} style={{
          color: collectorSortField === field ? colors.primary : colors.textMuted,
          opacity: collectorSortField === field ? 1 : 0.5
        }} />
      </div>
    </th>
  );

  // Leaflet Map Effect
  useEffect(() => {
    if (!mapContainerRef.current || !locations.length || !bounds.min_lat) return;

    // Filter locations based on selected collector
    const displayLocations = selectedCollector === 'all'
      ? locations
      : locations.filter(loc => loc.data_collector_id === selectedCollector);

    // Validate locations
    const validLocations = displayLocations.filter(loc => {
      const lat = parseFloat(loc.latitude);
      const lon = parseFloat(loc.longitude);
      return isFinite(lat) && isFinite(lon) &&
        lat >= -90 && lat <= 90 &&
        lon >= -180 && lon <= 180;
    });

    if (validLocations.length === 0) return;

    // Update custom marker styles
    const existingStyle = document.getElementById('geo-marker-styles');
    if (existingStyle) existingStyle.remove();

    const style = document.createElement('style');
    style.id = 'geo-marker-styles';
    style.textContent = `
      .leaflet-tile-pane .geo-tiles-dark {
        filter: invert(1) hue-rotate(180deg) brightness(0.85) contrast(0.9) saturate(0.6);
      }
      .geo-circle-marker {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #595cfcff;
        border: 0px solid #FFFFFF;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .geo-circle-marker:hover {
        transform: scale(1.3);
        box-shadow: 0 3px 12px rgba(59, 130, 246, 0.6);
      }
      .geo-circle-marker.misassigned {
        background: #A855F7;
      }
      .geo-circle-marker.outlier {
        background: ${colors.error};
      }
      .geo-circle-marker.duplicate {
        background: ${colors.warning};
      }
      .leaflet-marker-icon.geo-custom-marker {
        background: transparent;
        border: none;
      }
      .leaflet-popup-content-wrapper {
        background-color: ${isDarkMode ? '#1a1a1dff' : '#FFFFFF'} !important;
        border: 1px solid ${isDarkMode ? '#2A2A2E' : '#E5E7EB'} !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, ${isDarkMode ? '0.5' : '0.15'}) !important;
      }
      .leaflet-popup-tip {
        background-color: ${isDarkMode ? '#1a1a1dff' : '#FFFFFF'} !important;
        border: 1px solid ${isDarkMode ? '#2A2A2E' : '#E5E7EB'} !important;
      }
      .leaflet-container a.leaflet-popup-close-button {
        color: ${isDarkMode ? '#9CA3AF' : '#6B7280'} !important;
      }
    `;
    document.head.appendChild(style);

    // Clean up existing map — disable transitions first to prevent _leaflet_pos errors on resize
    if (mapInstanceRef.current) {
      try {
        const map = mapInstanceRef.current;
        // Disable all event handlers and animations before removal
        map.off();
        if (map._container) {
          map._container.style.transition = 'none';
        }
        map.remove();
      } catch (e) {
        // Silently handle — map may already be in a broken state during resize
      }
      mapInstanceRef.current = null;
    }

    // Ensure container is clean before creating new map
    if (mapContainerRef.current) {
      mapContainerRef.current.innerHTML = '';
    }

    // Create map
    const centerLat = (bounds.min_lat + bounds.max_lat) / 2;
    const centerLon = (bounds.min_lon + bounds.max_lon) / 2;

    const map = L.map(mapContainerRef.current, {
      center: [centerLat, centerLon],
      zoom: 10,
      scrollWheelZoom: true,
      zoomControl: true,
      fadeAnimation: false,
      zoomAnimation: false
    });

    mapInstanceRef.current = map;

    // Add tile layer based on theme
    // Carto's basemap tiles now demand an API key ("API KEY REQUIRED"
    // watermark), so both themes use keyless OpenStreetMap tiles. Dark mode
    // is produced with a CSS filter on the tile pane -- the standard Leaflet
    // trick: invert + hue-rotate keeps water blue and labels readable while
    // everything else goes dark. Free, no key, no per-tile watermarks.
    const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const attribution = '© OpenStreetMap contributors';

    L.tileLayer(tileUrl, {
      attribution: attribution,
      maxZoom: 19,
      className: isDarkMode ? 'geo-tiles-dark' : ''
    }).addTo(map);

    // Add markers for each location
    validLocations.forEach((location, index) => {
      const lat = parseFloat(location.latitude);
      const lon = parseFloat(location.longitude);

      // Determine if this is an outlier, duplicate, or null-island.
      // Null-island (points near 0,0) is its own category — they're
      // typically "GPS not acquired" placeholders, semantically different
      // from "real but distant" outliers.
      const isOutlier = showAnomalies && anomalies.outliers?.locations?.some(
        a => Math.abs(a.latitude - lat) < 0.0001 && Math.abs(a.longitude - lon) < 0.0001
      );
      const isDuplicate = showAnomalies && anomalies.duplicates?.locations?.some(
        d => Math.abs(d.latitude - lat) < 0.0001 && Math.abs(d.longitude - lon) < 0.0001
      );
      const isNullIsland = showAnomalies && anomalies.null_island?.locations?.some(
        n => Math.abs(n.latitude - lat) < 0.0001 && Math.abs(n.longitude - lon) < 0.0001
      );
      const misassignedRec = showAnomalies && anomalies.misassigned?.locations?.find(
        m => Math.abs(m.latitude - lat) < 0.0001 && Math.abs(m.longitude - lon) < 0.0001
      );

      let markerClass = 'geo-circle-marker';
      if (misassignedRec) markerClass += ' misassigned';
      else if (isOutlier) markerClass += ' outlier';
      else if (isNullIsland) markerClass += ' null-island';
      else if (isDuplicate) markerClass += ' duplicate';

      const circleIcon = L.divIcon({
        className: 'geo-custom-marker',
        html: `<div class="${markerClass}"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
        popupAnchor: [0, -6]
      });

      const marker = L.marker([lat, lon], { icon: circleIcon }).addTo(map);

      // Create popup content
      const statusLabel = misassignedRec ? `<span style="color: #A855F7; font-weight: 600;">In the wrong ${boundaryCheck.group_attr || 'cluster'}: ${misassignedRec.actually_in_group}</span>` :
        isOutlier ? '<span style="color: #EF4444; font-weight: 600; zIndex: 100;">Outlier</span>' :
        isNullIsland ? '<span style="color: #DC2626; font-weight: 600; zIndex: 102;">GPS not acquired (0,0)</span>' :
        isDuplicate ? '<span style="color: #F59E0B; font-weight: 600; zIndex: 101">Duplicate</span>' :
          '<span style="color: #10B981; font-weight: 600; zIndex: 99;">Normal</span>';

      const popupContent = `
        <div style="
          font-size: 14px;
          min-width: 200px;
          background-color: ${isDarkMode ? '#1a1a1dff' : '#FFFFFF'};
          color: ${isDarkMode ? '#F3F4F6' : '#374151'};
          padding: 12px;
          border-radius: 8px;
        ">
          <div style="font-weight: 600; margin-bottom: 10px; color: #3B82F6; font-size: 15px;">
            Location #${index + 1}
          </div>
          <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
            <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">Status:</span>
            ${statusLabel}
          </div>
          ${location.data_collector_id ? `
            <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">Collector:</span>
              <span style="font-weight: 500;">${location.data_collector_id}</span>
            </div>
          ` : ''}
          ${location.respondent_id ? `
            <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">Respondent:</span>
              <span style="font-weight: 500;">${location.respondent_id}</span>
            </div>
          ` : ''}
          ${location.group ? `
            <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">Group:</span>
              <span style="font-weight: 500;">${location.group}</span>
            </div>
          ` : ''}
          ${typeof location.distance_to_own_boundary_m === 'number' ? `
            <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">From own boundary:</span>
              <span style="font-weight: 500;">${location.distance_to_own_boundary_m >= 1000
                ? (location.distance_to_own_boundary_m / 1000).toFixed(1) + ' km'
                : Math.round(location.distance_to_own_boundary_m) + ' m'}</span>
            </div>
          ` : ''}
          ${typeof location.distance_from_group_center_m === 'number' ? `
            <div style="margin-bottom: 6px; display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};">From group centre:</span>
              <span style="font-weight: 500;">${location.distance_from_group_center_m >= 1000
                ? (location.distance_from_group_center_m / 1000).toFixed(1) + ' km'
                : Math.round(location.distance_from_group_center_m) + ' m'}</span>
            </div>
          ` : ''}
          <div style="
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid ${isDarkMode ? '#2A2A2E' : '#E5E7EB'};
            font-size: 11px;
            color: ${isDarkMode ? '#9CA3AF' : '#6B7280'};
            font-family: monospace;
          ">
            ${lat.toFixed(6)}, ${lon.toFixed(6)}
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, { maxWidth: 300 });
    });

    // Fit bounds to show all markers
    if (validLocations.length > 0) {
      const latLngs = validLocations.map(loc => [
        parseFloat(loc.latitude),
        parseFloat(loc.longitude)
      ]);
      map.fitBounds(latLngs, { padding: [50, 50] });
    }

    return () => {
      if (mapInstanceRef.current) {
        try {
          const map = mapInstanceRef.current;
          map.off();
          if (map._container) {
            map._container.style.transition = 'none';
          }
          map.remove();
        } catch (e) {
          // Silently handle
        }
        mapInstanceRef.current = null;
      }
    };
  }, [locations, selectedCollector, showAnomalies, bounds, isDarkMode, anomalies, colors]);

  // Interactive Map Component using Leaflet
  const InteractiveMap = () => {
    return (
      <div style={{ position: 'relative' }}>
        {/* Map Container */}
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '900px',
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDarkMode ? '#1a1a2e' : '#e8f4f8'
          }}
        />

        {/* Map Controls Overlay */}
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '60px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 1000
        }}>
          {/* Collector Filter */}
          <select
            value={selectedCollector}
            onChange={(e) => setSelectedCollector(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '13px',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            <option value="all">All Collectors ({locations.length})</option>
            {Object.entries(byCollector).map(([id, collectorData]) => (
              <option key={id} value={id}>{id} ({collectorData.location_count})</option>
            ))}
          </select>

          {/* Anomalies Toggle */}
          <button
            onClick={() => setShowAnomalies(!showAnomalies)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: showAnomalies ? colors.error : colors.cardBg,
              color: showAnomalies ? '#fff' : colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: `1px solid ${showAnomalies ? colors.error : colors.border}`
            }}
            title="Toggle Anomalies Highlighting"
          >
            <AlertTriangle size={14} />
            Anomalies
          </button>
        </div>

        {/* Legend */}
        <div className="nsx-metric-card" style={{
          position: 'absolute',
          bottom: '20px',
          left: '12px',
          backgroundColor: colors.cardBg,
          padding: '10px 14px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          fontSize: '12px',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', zIndex: '99' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#595cfcff' }} />
              <span style={{ color: colors.textSecondary }}>Normal</span>
            </div>
            {showAnomalies && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', zIndex: '100' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colors.error }} />
                  <span style={{ color: colors.textSecondary }}>Outlier</span>
                </div>
                {(anomalies.null_island?.count || 0) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', zIndex: '102' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#DC2626', border: '2px solid #fff' }} />
                    <span style={{ color: colors.textSecondary }}>GPS not acquired (0,0)</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', zIndex: '101' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colors.warning }} />
                  <span style={{ color: colors.textSecondary }}>Duplicate</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Export CSV function
  const exportCollectorCSV = () => {
    const headers = ['Collector', 'Locations', 'Coverage (km²)', 'Clusters', 'Cluster %', 'Center Lat', 'Center Lon'];
    const rows = sortedCollectors.map(c => [
      c.id,
      c.location_count,
      c.coverage_area_km2?.toFixed(2) || 0,
      c.clustering?.cluster_count || 0,
      c.clustering?.cluster_percentage?.toFixed(1) || 0,
      c.bounds?.center?.latitude?.toFixed(5) || '',
      c.bounds?.center?.longitude?.toFixed(5) || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'geolocation_collectors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============== RENDER OVERVIEW TAB ==============
  const renderOverviewTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          ((overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)) > 0 ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {(overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)} invalid
            </span>
          ) : null
        }
      >
      {/* Score and Summary Row */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '240px 1fr',
        gap: '16px'
      }}>
        {/* Quality Score Card — compact */}
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          padding: '20px',
          border: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'visible'
        }}>
          <PulseBeacon 
            severity={(qualityScore.overall_score || 0) < 60 ? 'critical' : (qualityScore.overall_score || 0) < 80 ? 'warning' : null} 
            isDarkMode={isDarkMode} 
          />
          <div style={{ fontSize: '12px', fontWeight: '500', color: colors.textSecondary, marginBottom: '8px' }}>
            Geolocation Quality Score
          </div>
          <ScoreRing score={qualityScore.overall_score || 0} size={100} strokeWidth={9} />
          <p style={{
            fontSize: '11px',
            color: colors.textMuted,
            textAlign: 'center',
            marginTop: '10px',
            lineHeight: 1.4
          }}>
            {qualityScore.interpretation?.description || 'Score based on coverage, dispersion, and anomaly rate.'}
          </p>
          
          {/* Component Scores — compact */}
          <div style={{ width: '100%', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${colors.border}` }}>
            {Object.entries(qualityScore.component_scores || {}).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'capitalize' }}>
                    {key.replace('_', ' ')}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>{value}</span>
                </div>
                <div style={{ height: '4px', backgroundColor: colors.border, borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${value}%`,
                    backgroundColor: value >= 75 ? colors.success : value >= 50 ? colors.warning : colors.error,
                    borderRadius: '2px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Two stacked summary cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Card 1: Data Quality — Valid, Coverage, Invalid */}
          <div className="nsx-metric-card" style={{
            flex: 1,
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
              Data Quality
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
              {/* Valid Coordinates */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: `${colors.success}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <CheckCircle size={18} style={{ color: colors.success }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={overall.valid_coordinates || 0} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Valid Coordinates</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>{formatNumber(summary.validity_rate)}% of total</div>
                </div>
              </div>
              {/* Coverage Area */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: `${colors.neutral}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Globe size={18} style={{ color: colors.neutral }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={bounds.coverage_area_km2 || 0} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Coverage Area (km²)</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>{formatNumber(bounds.lat_span_km)} × {formatNumber(bounds.lon_span_km)} km</div>
                </div>
              </div>
              {/* Invalid/Missing */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                <PulseBeacon 
                  severity={((overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)) > 0 ? 'critical' : null} 
                  isDarkMode={isDarkMode}
                  style={{ top: '-2px', left: '30px' }}
                />
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: ((overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)) > 0 ? `${colors.error}15` : `${colors.neutral}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <AlertTriangle size={18} style={{ color: ((overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)) > 0 ? colors.error : colors.neutral }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: ((overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)) > 0 ? colors.error : colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={(overall.invalid_coordinates || 0) + (overall.missing_coordinates || 0)} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Invalid/Missing</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>{overall.out_of_range_coordinates || 0} out of range</div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Spatial Health — Clusters, Anomalies, Spacing */}
          <div className="nsx-metric-card" style={{
            flex: 1,
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
              Spatial Health
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${(groupOutliers.enabled ? 4 : 3) + (boundaryCheck.enabled ? 1 : 0)}, 1fr)`, gap: '16px' }}>
              {/* Misassigned — inside a DIFFERENT cluster's polygon: fix the
                  cluster code, not the GPS. Distinct from red outliers. */}
              {boundaryCheck.enabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                  <PulseBeacon
                    severity={(anomalies.misassigned?.count || 0) > 0 ? 'warning' : null}
                    isDarkMode={isDarkMode}
                    style={{ top: '-2px', left: '30px' }}
                  />
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    backgroundColor: 'rgba(168, 85, 247, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <Target size={18} style={{ color: '#A855F7' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                      <AnimatedNumber value={anomalies.misassigned?.count || 0} duration={800} />
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted }}>In The Wrong {'\u201c'}{boundaryCheck.group_attr || 'cluster'}{'\u201d'}</div>
                    <div style={{ fontSize: '10px', color: colors.textSecondary }}>{boundaryCheck.outside?.count || 0} outside all boundaries</div>
                  </div>
                </div>
              )}
              {/* Group outliers — DataSure-style cluster-by-column: how many
                  points sit suspiciously far from their own group's centre */}
              {groupOutliers.enabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                  <PulseBeacon
                    severity={(groupOutliers.outlier_count || 0) > 0 ? 'warning' : null}
                    isDarkMode={isDarkMode}
                    style={{ top: '-2px', left: '30px' }}
                  />
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    backgroundColor: `${colors.error}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <MapPin size={18} style={{ color: colors.error }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                      <AnimatedNumber value={groupOutliers.outlier_count || 0} duration={800} />
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted }}>Far From Their {'\u201c'}{groupOutliers.column}{'\u201d'}</div>
                    <div style={{ fontSize: '10px', color: colors.textSecondary }}>
                      {formatNumber(groupOutliers.outlier_rate)}% of points \u00b7 {groupOutliers.groups_analyzed} groups
                    </div>
                  </div>
                </div>
              )}
              {/* Clusters Detected */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                <PulseBeacon 
                  severity={(clustering.cluster_percentage || 0) > 30 ? 'warning' : null} 
                  isDarkMode={isDarkMode}
                  style={{ top: '-2px', left: '30px' }}
                />
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: `${colors.warning}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Target size={18} style={{ color: colors.warning }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={clustering.total_clusters || 0} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Clusters Detected</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>{clustering.clustered_points || 0} points ({formatNumber(clustering.cluster_percentage)}%)</div>
                </div>
              </div>
              {/* Anomalies */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                <PulseBeacon 
                  severity={anomalies.severity === 'high' ? 'critical' : (anomalies.total_anomalies || 0) > 0 ? 'warning' : null} 
                  isDarkMode={isDarkMode}
                  style={{ top: '-2px', left: '30px' }}
                />
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: `${(anomalies.severity === 'high' ? colors.error : colors.warning)}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Activity size={18} style={{ color: anomalies.severity === 'high' ? colors.error : colors.warning }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={anomalies.total_anomalies || 0} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Anomalies</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>{formatNumber(anomalies.anomaly_rate)}% anomaly rate</div>
                </div>
              </div>
              {/* Avg Spacing */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  backgroundColor: `${colors.neutral}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Navigation size={18} style={{ color: colors.neutral }} />
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: colors.text, lineHeight: 1.1 }}>
                    <AnimatedNumber value={distanceMetrics.average_spacing_km || 0} duration={800} />
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>Avg Spacing (km)</div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>Diameter: {formatNumber(distanceMetrics.coverage_diameter_km)} km</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      </AnimatedCard>
      </CollapsibleSection>

      <CollapsibleSection title="Map & Analysis" isDarkMode={isDarkMode} colors={colors}>
      {/* Map Section */}
      <AnimatedCard delay={160} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: colors.cardBg,
        borderRadius: '16px',
        padding: '18px',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
              Location Distribution
            </h3>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '4px 0 0 0' }}>
              {locations.length} interview locations • {Object.keys(byCollector).length} collectors
            </p>
          </div>
        </div>
        
        <InteractiveMap />

        {/* Bounds Info */}
        {bounds.center && (
          <div className="nsx-metric-card" style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: colors.cardBgAlt,
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              <span style={{ fontWeight: '500' }}>Center:</span>{' '}
              {bounds.center.latitude.toFixed(5)}, {bounds.center.longitude.toFixed(5)}
            </div>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              <span style={{ fontWeight: '500' }}>Lat Range:</span>{' '}
              {bounds.min_lat?.toFixed(4)} to {bounds.max_lat?.toFixed(4)}
            </div>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              <span style={{ fontWeight: '500' }}>Lon Range:</span>{' '}
              {bounds.min_lon?.toFixed(4)} to {bounds.max_lon?.toFixed(4)}
            </div>
          </div>
        )}
      </div>
      </AnimatedCard>
      </CollapsibleSection>
    </div>
  );

  // ============== RENDER SPATIAL TAB ==============
  const renderSpatialTab = () => {
    const quadrants = spatialAnalysis.quadrant_distribution || {};
    const fromCenter = distanceMetrics.from_center || {};
    const nearestNeighbor = distanceMetrics.nearest_neighbor || {};

    // Helper to get distance values (handles both old and new key formats)
    const getDistVal = (obj, key) => {
      // Try new format first (min, max, mean), then old format (min_km, max_km, mean_km)
      return obj?.[key] ?? obj?.[`${key}_km`] ?? null;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Spatial Metrics Row */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px' }}>
          <MetricCard
            animDelay={0}
            icon={Target}
            title="Coverage Score"
            value={formatNumber(spatialAnalysis.coverage_score)}
            subtitle="Spatial spread quality"
            color={getScoreColor(spatialAnalysis.coverage_score || 0)}
          />
          <MetricCard
            animDelay={80}
            icon={Activity}
            title="Dispersion Index"
            value={formatNumber(spatialAnalysis.dispersion_index, 2)}
            subtitle="Coefficient of variation"
            color={colors.neutral}
          />
          <MetricCard
            animDelay={160}
            icon={Compass}
            title="Mean Distance"
            value={`${formatNumber(spatialAnalysis.mean_distance_from_center || spatialAnalysis.mean_distance_from_center_km)} km`}
            subtitle="From center point"
            color={colors.neutral}
          />
          <MetricCard
            animDelay={240}
            icon={Navigation}
            title="Coverage Diameter"
            value={`${formatNumber(distanceMetrics.coverage_diameter_km)} km`}
            subtitle="Max point-to-point"
            color={colors.neutral}
          />
        </div>
        </AnimatedCard>

        <AnimatedCard delay={80} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
          {/* Quadrant Distribution */}
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px' }}>
              Quadrant Distribution
            </h3>
            <p style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '16px', lineHeight: 1.4 }}>
              Geographic spread of interview locations across four quadrants from the center point. A balanced survey covers all quadrants evenly (~25% each).
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              maxWidth: '300px',
              margin: '0 auto'
            }}>
              {[
                { key: 'NW', label: 'Northwest' },
                { key: 'NE', label: 'Northeast' },
                { key: 'SW', label: 'Southwest' },
                { key: 'SE', label: 'Southeast' }
              ].map(({ key, label }) => {
                const value = quadrants[key] || 0;
                const isPercentage = typeof value === 'number' && value <= 100;
                const displayValue = isPercentage ? value.toFixed(1) : value;
                // Deviation from balanced (25%) — higher deviation = more warning
                const deviation = isPercentage ? Math.abs(value - 25) : 0;
                const deviationColor = deviation < 8 ? colors.neutral : deviation < 15 ? '#F59E0B' : '#F97316';
                const bgOpacity = isDarkMode ? 0.15 : 0.08;
                
                return (
                  <div
                    key={key}
                    style={{
                      padding: '14px',
                      borderRadius: '10px',
                      backgroundColor: isDarkMode ? `${deviationColor}${Math.round(bgOpacity * 255).toString(16).padStart(2, '0')}` : `${deviationColor}${Math.round(bgOpacity * 255).toString(16).padStart(2, '0')}`,
                      border: `1px solid ${deviationColor}25`,
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '4px', fontWeight: '500' }}>
                      {label}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: colors.text }}>
                      {displayValue}
                    </div>
                    <div style={{ fontSize: '12px', color: deviationColor, fontWeight: '500' }}>
                      {isPercentage ? '%' : 'points'}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Balance indicator */}
            {(() => {
              const values = ['NW', 'NE', 'SW', 'SE'].map(k => quadrants[k] || 0);
              const maxDev = Math.max(...values.map(v => Math.abs(v - 25)));
              const balanceLabel = maxDev < 8 ? 'Well balanced' : maxDev < 15 ? 'Moderately skewed' : 'Heavily skewed';
              const balanceColor = maxDev < 8 ? colors.neutral : maxDev < 15 ? '#F59E0B' : '#F97316';
              return (
                <div style={{
                  marginTop: '12px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: balanceColor,
                  fontWeight: '500'
                }}>
                  {balanceLabel} — max deviation {maxDev.toFixed(0)}% from 25%
                </div>
              );
            })()}
          </div>

          {/* Clustering Analysis */}
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '20px' }}>
              Clustering Analysis
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: colors.warning }}>
                  {clustering.total_clusters || 0}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Total Clusters</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: colors.text }}>
                  {clustering.clustered_points || 0}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Clustered Points</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: colors.neutral }}>
                  {clustering.threshold_meters || 100}m
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Threshold</div>
              </div>
            </div>

            {clustering.severity && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: `${getSeverityColor(clustering.severity)}15`,
                border: `1px solid ${getSeverityColor(clustering.severity)}30`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <AlertTriangle size={18} style={{ color: getSeverityColor(clustering.severity) }} />
                <span style={{ fontSize: '15px', color: colors.text }}>
                  Clustering severity: <strong style={{ textTransform: 'capitalize' }}>{clustering.severity}</strong>
                  {' '}({formatNumber(clustering.cluster_percentage)}% of points are clustered)
                </span>
              </div>
            )}
          </div>
        </div>
        </AnimatedCard>

        {/* Distance Metrics & Anomalies */}
        <AnimatedCard delay={240} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
          {/* Distance Statistics */}
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '20px' }}>
              Distance Statistics
            </h3>
            
            {/* Distance from Center */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: colors.textSecondary, marginBottom: '10px' }}>
                Distance from Center (km)
              </div>
              {/* Visual range bar */}
              {(() => {
                const minVal = getDistVal(fromCenter, 'min') || 0;
                const maxVal = getDistVal(fromCenter, 'max') || 1;
                const meanVal = getDistVal(fromCenter, 'mean') || 0;
                const medianVal = getDistVal(fromCenter, 'median') || 0;
                const range = maxVal - minVal || 1;
                const meanPct = ((meanVal - minVal) / range) * 100;
                const medianPct = ((medianVal - minVal) / range) * 100;
                return (
                  <div style={{ position: 'relative', height: '28px', marginBottom: '12px', borderRadius: '6px', backgroundColor: isDarkMode ? '#2A2A2E' : '#F3F4F6', overflow: 'visible' }}>
                    {/* Range fill */}
                    <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', borderRadius: '6px', background: isDarkMode ? 'linear-gradient(90deg, #2A2A2E, #3A3A3E)' : 'linear-gradient(90deg, #E5E7EB, #D1D5DB)' }} />
                    {/* Mean marker */}
                    <div style={{ position: 'absolute', left: `${Math.min(95, Math.max(5, meanPct))}%`, top: '-2px', width: '3px', height: '32px', backgroundColor: colors.warning, borderRadius: '2px', zIndex: 2 }} title={`Mean: ${formatNumber(meanVal)} km`} />
                    {/* Median marker */}
                    <div style={{ position: 'absolute', left: `${Math.min(95, Math.max(5, medianPct))}%`, top: '-2px', width: '3px', height: '32px', backgroundColor: isDarkMode ? '#D1D5DB' : '#374151', borderRadius: '2px', zIndex: 2 }} title={`Median: ${formatNumber(medianVal)} km`} />
                    {/* Labels */}
                    <div style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', fontWeight: '600', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>{formatNumber(minVal)}</div>
                    <div style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', fontWeight: '600', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>{formatNumber(maxVal)}</div>
                  </div>
                );
              })()}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '6px' }}>
                {[
                  { stat: 'min', label: 'MIN' },
                  { stat: 'max', label: 'MAX' },
                  { stat: 'mean', label: 'MEAN', accent: colors.warning },
                  { stat: 'median', label: 'MEDIAN', accent: isDarkMode ? '#D1D5DB' : '#374151' },
                  { stat: 'std', label: 'STD' }
                ].map(({ stat, label, accent }) => (
                  <div className="nsx-metric-card" key={stat} style={{
                    textAlign: 'center',
                    padding: '8px 6px',
                    backgroundColor: colors.cardBgAlt,
                    borderRadius: '8px',
                    borderTop: accent ? `2px solid ${accent}` : 'none'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: accent || colors.text }}>
                      {formatNumber(getDistVal(fromCenter, stat))}
                    </div>
                    <div style={{ fontSize: '9px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Nearest Neighbor */}
            <div>
              <div style={{ fontSize: '13px', fontWeight: '500', color: colors.textSecondary, marginBottom: '10px' }}>
                Nearest Neighbor Distance (km)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '6px' }}>
                {[
                  { stat: 'min', label: 'MIN' },
                  { stat: 'max', label: 'MAX' },
                  { stat: 'mean', label: 'MEAN' },
                  { stat: 'median', label: 'MEDIAN' }
                ].map(({ stat, label }) => (
                  <div className="nsx-metric-card" key={stat} style={{
                    textAlign: 'center',
                    padding: '8px 6px',
                    backgroundColor: colors.cardBgAlt,
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
                      {formatNumber(getDistVal(nearestNeighbor, stat), 3)}
                    </div>
                    <div style={{ fontSize: '9px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Anomalies Breakdown */}
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            padding: '18px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '20px' }}>
              Anomalies Breakdown
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (anomalies.null_island?.count > 0 ? '1fr 1fr 1fr' : '1fr 1fr'), gap: '16px', marginBottom: '20px' }}>
              <div style={{
                padding: '16px',
                backgroundColor: `${colors.error}10`,
                borderRadius: '12px',
                border: `1px solid ${colors.error}30`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: colors.error }}>
                  {anomalies.outliers?.count || 0}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Distance Outliers</div>
                <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
                  Threshold: {formatNumber(anomalies.outliers?.threshold_km)} km
                </div>
              </div>
              {(anomalies.null_island?.count || 0) > 0 && (
                <div style={{
                  padding: '16px',
                  backgroundColor: `${colors.error}10`,
                  borderRadius: '12px',
                  border: `1px solid ${colors.error}30`,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: colors.error }}>
                    {anomalies.null_island.count}
                  </div>
                  <div style={{ fontSize: '13px', color: colors.textSecondary }}>GPS Not Acquired</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
                    Points near (0, 0) — likely device GPS failure
                  </div>
                </div>
              )}
              <div style={{
                padding: '16px',
                backgroundColor: `${colors.warning}10`,
                borderRadius: '12px',
                border: `1px solid ${colors.warning}30`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: colors.warning }}>
                  {anomalies.duplicates?.count || 0}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Duplicate Coordinates</div>
                <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
                  Identical to ~11 cm — may be shared GPS, not error
                </div>
              </div>
            </div>

            <div className="nsx-metric-card" style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: colors.cardBgAlt,
              fontSize: '13px',
              color: colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Info size={16} />
              Total anomaly rate: {formatNumber(anomalies.anomaly_rate)}%
            </div>
          </div>
        </div>
        </AnimatedCard>
      </div>
    );
  };

  // ============== RENDER COLLECTOR TAB ==============
  const renderCollectorTab = () => {
    const comparison = collectorComparison || {};
    const rankings = comparison.rankings || [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px' }}>
          <MetricCard
            animDelay={0}
            icon={Users}
            title="Total Collectors"
            value={Object.keys(byCollector).length}
            subtitle="With geolocation data"
            color={colors.neutral}
          />
          <MetricCard
            animDelay={80}
            icon={MapPin}
            title="Avg Locations"
            value={formatNumber(comparison.average_locations_per_collector)}
            subtitle="Per collector"
            color={colors.neutral}
          />
          <MetricCard
            animDelay={160}
            icon={Globe}
            title="Avg Coverage"
            value={`${formatNumber(comparison.average_coverage_km2)} km²`}
            subtitle="Per collector"
            color={colors.neutral}
          />
          <MetricCard
            animDelay={240}
            icon={CheckCircle}
            title="Best Coverage"
            value={comparison.best_coverage || '—'}
            subtitle="Collector ID"
            color={colors.neutral}
          />
        </div>

        {/* Collector Table */}
        <AnimatedCard delay={0} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
              Collector Performance
            </h3>
            <button
              onClick={exportCollectorCSV}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: 'transparent',
                color: colors.textSecondary,
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <SortableHeader field="id" label="Collector" />
                  <SortableHeader field="location_count" label="Locations" />
                  <SortableHeader field="coverage_area_km2" label="Coverage (km²)" />
                  <SortableHeader field="cluster_percentage" label="Clusters (%)" />
                  <th style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '450',
                    color: colors.textSecondary,
                    borderBottom: `2px solid ${colors.border}`
                  }}>
                    Center
                  </th>
                  <th style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '450',
                    color: colors.textSecondary,
                    borderBottom: `2px solid ${colors.border}`
                  }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectors.map(collector => {
                  const avgCoverage = comparison.average_coverage_km2 || 0;
                  const coverage = collector.coverage_area_km2 || 0;
                  const clusterPct = collector.clustering?.cluster_percentage || 0;
                  
                  let status, statusColor;
                  if (clusterPct > 20) {
                    status = 'High Clustering';
                    statusColor = colors.error;
                  } else if (coverage > avgCoverage * 1.2) {
                    status = 'Good Coverage';
                    statusColor = colors.success;
                  } else if (coverage < avgCoverage * 0.5) {
                    status = 'Low Coverage';
                    statusColor = colors.warning;
                  } else {
                    status = 'Normal';
                    statusColor = colors.neutral;
                  }

                  return (
                    <tr key={collector.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '500', color: colors.text }}>
                        {collector.id}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                        {collector.location_count}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: colors.text }}>
                        {formatNumber(coverage)}
                      </td>
                      <td style={{
                        padding: '14px 16px',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: clusterPct > 20 ? colors.error : clusterPct > 10 ? colors.warning : colors.success
                      }}>
                        {formatNumber(clusterPct)}%
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '12px', color: colors.textSecondary }}>
                        {collector.bounds?.center ? (
                          `${collector.bounds.center.latitude?.toFixed(4)}, ${collector.bounds.center.longitude?.toFixed(4)}`
                        ) : '—'}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: `${statusColor}15`,
                          color: statusColor
                        }}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </AnimatedCard>
      </div>
    );
  };

  // ============== MAIN RENDER ==============
  return (
    <div className='geolocation-section' style={{ 
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: isMobile ? 'visible' : 'hidden',
     }}>
      {/* Dimension Header */}
      <DimensionHeader
        slug="geolocation"
        title="Geolocation"
        description="Validates GPS coordinates against expected boundaries and detects spatial anomalies. Catches interviews conducted outside assigned areas, clustered points, or falsified coordinates."
        rememberCollapseKey="geolocation"
        stat={{
          score: qualityScore.overall_score,
          scoreLabel: 'spatial score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'collector') return { ...t, count: Object.keys(byCollector).length, countTone: 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      {/* Tab Content */}
      <div style={{ 
          minHeight: '600px',
          marginTop: '16px'
        }}>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'spatial' && renderSpatialTab()}
        {activeTab === 'collector' && renderCollectorTab()}
      </div>
      <CoachingPanel
        suggestions={coachingSuggestions}
        detectorSource="geolocation"
        isMobile={isMobile}
      />

    </div>
  );
};

export default GeolocationSection;